import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';

import { getNumberPlaces } from '../Slider/utils';
import SliderInput from '../Slider/SliderInput';
import { SLIDER_OPERATORS } from '../constants';
import { useStyles, useSliderStyles, inputWidth } from '../Slider/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useDelayedCall } from '../../../hooks/useDelayCall';
import useUpdateFilterValue from '../../../hooks/useUpdateFilterValue';

const RangeSlider = ({
	width,
	horizontal,
	hiddenlabel,
	constraints,
	appliedValue,
	disabled,
	label,
	longLabel,
	values,
	unit,
	cid,
	categoryId
}) => {
	const classes = useStyles({
		isHorizontal: horizontal && isWidthDown(width, 'md'),
		isRange: true,
		spacing: null
	})();
	const { t } = useTranslation();

	const sliderClasses = useSliderStyles();
	const min = constraints.minValue;
	const max = constraints.maxValue;
	const [localSliderValue, setLocalSliderValue] = useState([min, max]);
	const [localSliderChangeCommittedValue, setLocalSliderChangeCommittedValue] = useState([min, max]);
	const id = label + '_slider';
	const aria = label + '-slider';
	const step = getNumberPlaces((max - min) / 10);
	const textInputLeft = useRef();
	const textInputRight = useRef();
	const { updateFilterValue } = useUpdateFilterValue();
	const [inputChanged, setInputChanged] = useState(false);
	const appliedState = useSelector(state => state.filters.appliedState);

	useEffect(() => {
		if (JSON.stringify(appliedValue) !== JSON.stringify(localSliderValue)) setLocalSliderValue([...appliedValue]);
		setLocalSliderChangeCommittedValue([...appliedValue]);
		// eslint-disable-next-line
	}, [appliedValue]);

	const handleSliderChange = (_event, value) => {
		setLocalSliderValue(value);
	};

	const handleSliderChangeCommitted = (_event, value) => {
		updateFilterValue(cid, categoryId, [...value]);
		setInputChanged(false);
		focusToSliderInput(value);
	};

	const focusToSliderInput = value => {
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		if (isMobile) {
			return;
		}
		if (value[0] !== localSliderChangeCommittedValue[0] && textInputLeft) {
			textInputLeft.current.focus();
		} else if (value[1] !== localSliderChangeCommittedValue[1] && textInputRight) {
			textInputRight.current.focus();
		}
	};

	const handleDelayChange = newValue => {
		const validatedValue = newValue;
		const leftValue = newValue[0];
		const rightValue = newValue[1];
		if (newValue[0] !== '' && newValue[1] !== '') {
			validateValue(validatedValue, leftValue, rightValue);
			updateFilterValue(cid, categoryId, validatedValue);
		}
	};

	const changeOnDelay = useDelayedCall(handleDelayChange);

	const validateValue = (validatedValue, leftValue, rightValue) => {
		if (leftValue < min || leftValue === '') {
			validatedValue[0] = min;
		} else if (leftValue > max) {
			validatedValue[0] = leftValue > rightValue ? rightValue : max;
		} else if (rightValue > max || rightValue === '') {
			validatedValue[1] = max;
		} else if (rightValue < min) {
			validatedValue[1] = rightValue < leftValue ? leftValue : min;
		} else if (leftValue > rightValue) {
			validatedValue[1] = leftValue;
		}
	};

	const handleInputChange = index => event => {
		const parsedValue = event.target.value === '' ? '' : Number(event.target.value);
		const newSliderValue = [...localSliderValue];
		newSliderValue[index] = parsedValue;

		setLocalSliderValue(newSliderValue);
		changeOnDelay(newSliderValue);
		setInputChanged(true);
	};

	const handleInputBlur = () => {
		if (inputChanged) {
			const validatedValue = [...localSliderValue];
			const leftValue = localSliderValue[0];
			const rightValue = localSliderValue[1];
			validateValue(validatedValue, leftValue, rightValue);
			updateFilterValue(cid, categoryId, validatedValue);
		}
	};

	const handleSubmitValueWithEnter = event => {
		if (event.key === 'Enter') {
			document.activeElement.blur();
		}
	};

	const isInputInvalid = index =>
		localSliderValue[index] === '' || (localSliderValue[0] > localSliderValue[1] && !localSliderValue.includes(''));

	return (
		<Grid
			container
			direction={horizontal && isWidthDown(width, 'sm') ? 'row' : 'column'}
			justifyContent={horizontal ? 'space-between' : 'flex-start'}
			alignItems={horizontal ? 'flex-start' : 'center'}
			spacing={horizontal && isWidthDown(width, 'sm') ? 2 : 0}>
			{hiddenlabel || (
				<Grid item /* xs={horizontal ? 4 : 12}*/>
					{longLabel && (
						<Typography display="inline" variant="body1">
							{parse(longLabel) + '\u00a0\u00a0'}
						</Typography>
					)}

					<Typography display="inline" variant="body2" color={longLabel ? 'textSecondary' : 'textPrimary'}>
						{parse(label)}
					</Typography>
					{(isInputInvalid(0) || isInputInvalid(1)) && (
						<FormHelperText style={{ color: 'red' }}>
							{t('rangeSlider.outOfRange') + ': [' + min + ' ... ' + max + ']'}
						</FormHelperText>
					)}
				</Grid>
			)}

			<Grid
				item
				// xs={horizontal ? 8 : 12}
				container
				className={classes.container}>
				<Box
					display="flex"
					clone
					order={{
						xs: 1,
						sm: 1,
						md: horizontal ? 2 : 1,
						lg: horizontal ? 2 : 1,
						xl: horizontal ? 2 : 1
					}}>
					<Grid
						item
						xs={12}
						sm={12}
						md={horizontal ? 6 : 12}
						lg={horizontal ? 6 : 12}
						xl={horizontal ? 6 : 12}
						className={classes.sliderContainer}>
						<Slider
							color={appliedState[cid]?.used ? 'primary' : 'secondary'}
							disabled={values.length < 2 || disabled}
							min={min}
							max={max}
							step={step}
							value={[...localSliderValue]}
							onChange={handleSliderChange}
							onChangeCommitted={handleSliderChangeCommitted}
							aria-labelledby={aria}
							classes={sliderClasses}
						/>
					</Grid>
				</Box>

				<Box
					display="flex"
					clone
					order={{
						xs: 2,
						sm: 2,
						md: horizontal ? 1 : 2,
						lg: horizontal ? 1 : 2,
						xl: horizontal ? 1 : 2
					}}>
					<Grid item xs={6} sm={6} md={horizontal ? 3 : 6} lg={horizontal ? 3 : 6} xl={horizontal ? 3 : 6}>
						<SliderInput
							id={id + '-sliderinput-min'}
							disabled={values.length < 2 || disabled}
							error={isInputInvalid(0)}
							value={localSliderValue[0]}
							onChange={handleInputChange(0)}
							onBlur={handleInputBlur}
							onKeyPress={handleSubmitValueWithEnter}
							width={inputWidth}
							aria={aria}
							unit={unit}
							startAdornmentText={SLIDER_OPERATORS[constraints.minOperator].symbol}
							inputRef={textInputLeft}
						/>
					</Grid>
				</Box>

				<Box display="flex" justifyContent="flex-end" clone order={{ xs: 3, sm: 3, md: 3, lg: 3, xl: 3 }}>
					<Grid item xs={6} sm={6} md={horizontal ? 3 : 6} lg={horizontal ? 3 : 6} xl={horizontal ? 3 : 6}>
						<SliderInput
							id={id + '-sliderinput-max'}
							disabled={values.length < 2 || disabled}
							error={isInputInvalid(1)}
							value={localSliderValue[1]}
							onBlur={handleInputBlur}
							onChange={handleInputChange(1)}
							onKeyPress={handleSubmitValueWithEnter}
							width={inputWidth}
							aria={aria}
							unit={unit}
							startAdornmentText={SLIDER_OPERATORS[constraints.maxOperator].symbol}
							inputRef={textInputRight}
						/>
					</Grid>
				</Box>
			</Grid>
		</Grid>
	);
};

RangeSlider.propTypes = {
	width: PropTypes.string.isRequired,
	horizontal: PropTypes.bool,
	hiddenlabel: PropTypes.bool,
	constraints: PropTypes.shape({
		minOperator: PropTypes.oneOf(Object.keys(SLIDER_OPERATORS)),
		maxOperator: PropTypes.oneOf(Object.keys(SLIDER_OPERATORS)),
		minValue: PropTypes.number,
		maxValue: PropTypes.number
	}).isRequired,
	appliedValue: PropTypes.arrayOf(PropTypes.number).isRequired,
	disabled: PropTypes.bool.isRequired,
	label: PropTypes.string.isRequired,
	longLabel: PropTypes.string.isRequired,
	values: PropTypes.arrayOf(PropTypes.number).isRequired,
	unit: PropTypes.string
};

export default withWidth()(React.memo(RangeSlider));
